import { ApiError, isErrorReason, type ErrorCode } from "@tovi/shared";
import { staffHeaders } from "./staff.ts";

/**
 * The till's API client (`02-architecture.md` § POS terminal packaging).
 *
 * One of the product's two cross-origin clients, beside `apps/staff`: it runs inside a Capacitor
 * WebView whose origin is `https://localhost`, so it cannot hide behind a Next
 * rewrite and uses a **bearer token** rather than a cookie. The API's
 * `POS_CORS_ORIGIN` exists for exactly this and nothing else.
 *
 * Everything that changes something goes through the offline queue instead of
 * this — see `lib/till.ts`. This is for reads, and for the queue's own
 * transport.
 */

const TOKEN_KEY = "tovi.pos.token";

export function baseUrl(): string {
  // In the browser this is proxied by Vite; under Capacitor it is the real
  // API, injected at build time.
  return import.meta.env.VITE_API_URL ?? "";
}

export function token(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(value: string | null): void {
  if (value === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, value);
}

type SignOutListener = () => void;

const signOutListeners = new Set<SignOutListener>();

/**
 * Told when the terminal's credential stopped working.
 *
 * A bearer token expires or is revoked while the till is switched on, and
 * every call after that moment is a 401. Without this the screen simply goes
 * quiet — the menu never refreshes, the queue never drains, and nothing says
 * why. The app subscribes and puts the sign-in screen in front of somebody.
 */
export function onSignedOut(listener: SignOutListener): () => void {
  signOutListeners.add(listener);
  return () => {
    signOutListeners.delete(listener);
  };
}

/**
 * Drops the credential and says so, once.
 *
 * Idempotent on purpose: a drain and a screen's query can both hit a 401 in
 * the same second, and the second one must not re-announce a sign-out that has
 * already happened.
 */
export function signOut(): void {
  if (token() === null) return;
  setToken(null);
  for (const listener of signOutListeners) listener();
}

export type ApiOptions = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  idempotencyKey?: string;
  /** Anything else the call needs, merged over the ones every call carries. */
  headers?: Record<string, string>;
};

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const held = token();
  const response = await fetch(`${baseUrl()}/api${path}`, {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...(held ? { Authorization: `Bearer ${held}` } : {}),
      // On every call, not only the shift routes: the assertion is what says
      // whose authority this request carries, and a read that omits it is
      // answered with the terminal's.
      ...staffHeaders(),
      ...(options.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : {}),
      ...options.headers,
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    signal: options.signal,
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => null)) as
    { error?: { code?: string; message?: string; details?: unknown } } | T | null;

  if (!response.ok) {
    // A 401 is not this call's problem to report: no retry and no wording will
    // fix it, so the terminal is signed out and the caller's error becomes a
    // detail behind the sign-in screen.
    if (response.status === 401) signOut();

    const envelope = (payload ?? {}) as {
      error?: { code?: string; message?: string; reason?: string };
    };
    const error = new ApiError(
      // The status decides this one rather than the envelope, so that the
      // sign-out above and the caller's view of the same answer cannot
      // disagree: a 401 that arrived without a body would otherwise be
      // reported as `INTERNAL` and retried after the terminal had already
      // been signed out.
      response.status === 401
        ? "UNAUTHENTICATED"
        : ((envelope.error?.code as ErrorCode) ?? "INTERNAL"),
      // Empty rather than an English sentence when the answer carried no body:
      // this module has no `t`, and `resolveErrorMessage` renders the screen's
      // own fallback in the cashier's language for exactly this case.
      envelope.error?.message ?? "",
    );
    const reason = envelope.error?.reason;
    throw isErrorReason(reason) ? error.because(reason) : error;
  }

  return payload as T;
}

/** Whether a failure was the network rather than the server saying no. */
export function isOffline(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof ApiError && error.code === "INTERNAL");
}
